import { useEffect, useRef } from 'react';
import axios from 'axios';

/**
 * Tracks scroll progress on the current page and PUTs it every ~5s
 * (and once on unmount) if the user is logged in.
 */
export function useReadingProgress(articleId, user) {
  const lastSent = useRef(0);
  const lastProgress = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!articleId || !user) return;

    const compute = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return 0;
      return Math.max(0, Math.min(1, scrollTop / docHeight));
    };

    const send = async (force = false) => {
      const p = compute();
      lastProgress.current = p;
      const now = Date.now();
      if (!force && now - lastSent.current < 4000) return;
      if (Math.abs(p - lastSent.current) < 0.02 && !force) return; // skip tiny changes
      lastSent.current = now;
      try {
        await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/progress`,
          { progress: p },
          { withCredentials: true }
        );
      } catch (e) {
        // Progress tracking failure is non-critical
        console.debug('Failed to track reading progress:', e);
      }
    };

    const onScroll = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(send, 800);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Send initial
    setTimeout(send, 1500);

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (timer.current) clearTimeout(timer.current);
      // Final send on unmount
      if (lastProgress.current > 0) {
        axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/articles/${articleId}/progress`,
          { progress: lastProgress.current },
          { withCredentials: true }
        ).catch(() => {});
      }
    };
  }, [articleId, user]);
}
